/**
 * API Service Layer
 *
 * Provides a centralized service for all API calls to mock data endpoints.
 * Simulates real API behavior with error handling, caching, filtering, and loading states.
 *
 * Features:
 * - RESTful API simulation
 * - Automatic error handling and retry logic
 * - Client-side caching with TTL
 * - Query parameter support for filtering and pagination
 * - Loading state management
 * - Realistic response timing simulation
 */

// API Configuration
const API_CONFIG = {
  BASE_URL: "/api",
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes
  REQUEST_TIMEOUT: 30000, // 30 seconds
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
};

// In-memory cache
const cache = new Map();

/**
 * Utility function to generate cache keys
 */
const getCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join("&");
  return `${endpoint}?${sortedParams}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cacheEntry) => {
  return cacheEntry && Date.now() - cacheEntry.timestamp < API_CONFIG.CACHE_TTL;
};

/**
 * Simulate network delay for realistic API behavior
 */
const simulateNetworkDelay = () => {
  const delay = Math.random() * 500 + 200; // 200-700ms
  return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Enhanced fetch with timeout, retries, and error handling
 */
const fetchWithRetry = async (
  url,
  options = {},
  retries = API_CONFIG.MAX_RETRIES
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    API_CONFIG.REQUEST_TIMEOUT
  );

  try {
    await simulateNetworkDelay();

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (retries > 0 && !error.name === "AbortError") {
      await new Promise((resolve) =>
        setTimeout(resolve, API_CONFIG.RETRY_DELAY)
      );
      return fetchWithRetry(url, options, retries - 1);
    }

    throw error;
  }
};

/**
 * Generic API request handler
 */
const apiRequest = async (endpoint, params = {}) => {
  const cacheKey = getCacheKey(endpoint, params);

  // Check cache first
  const cachedData = cache.get(cacheKey);
  if (isCacheValid(cachedData)) {
    return { ...cachedData.data, fromCache: true };
  }

  try {
    const url = `${API_CONFIG.BASE_URL}/${endpoint}.json`;
    const response = await fetchWithRetry(url);
    const data = await response.json();

    // Apply filters if provided
    const filteredData = applyFilters(data, params);

    // Cache the result
    cache.set(cacheKey, {
      data: filteredData,
      timestamp: Date.now(),
    });

    return { ...filteredData, fromCache: false };
  } catch (error) {
    throw new APIError(
      error.message,
      error.name === "AbortError" ? "TIMEOUT" : "NETWORK_ERROR"
    );
  }
};

/**
 * Apply filters to data based on query parameters
 */
const applyFilters = (data, params) => {
  if (!data) return data;

  let items = data[Object.keys(data)[0]]; // Get the main data array
  if (!Array.isArray(items)) return data;

  // Only apply filters if params exist and have filter values
  if (params && Object.keys(params).length > 0) {
    // Apply filters
    Object.entries(params).forEach(([key, value]) => {
      if (key === "status" && value) {
        const statuses = Array.isArray(value) ? value : value.split(",");
        items = items.filter((item) => statuses.includes(item.status));
      }

      if (key === "type" && value) {
        const types = Array.isArray(value) ? value : value.split(",");
        items = items.filter((item) => types.includes(item.type));
      }

      if (key === "search" && value) {
        const searchTerm = value.toLowerCase();
        items = items.filter(
          (item) =>
            item.name?.toLowerCase().includes(searchTerm) ||
            item.description?.toLowerCase().includes(searchTerm) ||
            item.location?.address?.toLowerCase().includes(searchTerm)
        );
      }

      if (key === "limit" && value) {
        const limit = parseInt(value, 10);
        if (!isNaN(limit)) {
          items = items.slice(0, limit);
        }
      }

      if (key === "offset" && value) {
        const offset = parseInt(value, 10);
        if (!isNaN(offset)) {
          items = items.slice(offset);
        }
      }
    });
  }

  return {
    ...data,
    [Object.keys(data)[0]]: items,
    meta: {
      ...data.meta,
      filtered: items.length,
      filters: params,
    },
  };
};

/**
 * Custom API Error class
 */
class APIError extends Error {
  constructor(message, code = "UNKNOWN_ERROR", details = null) {
    super(message);
    this.name = "APIError";
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

// ===== PROJECTS API =====

/**
 * Get all projects with optional filtering
 */
export const getProjects = async (params = {}) => {
  return apiRequest("projects", params);
};

/**
 * Get a single project by ID
 */
export const getProject = async (id) => {
  if (!id) {
    throw new APIError("Project ID is required", "VALIDATION_ERROR");
  }

  const data = await apiRequest("projects");
  const project = data.projects.find((p) => p.id === id);

  if (!project) {
    throw new APIError(`Project with ID '${id}' not found`, "NOT_FOUND");
  }

  return { project };
};

/**
 * Create a new project
 */
export const createProject = async (projectData) => {
  // Simulate API creation
  await simulateNetworkDelay();

  const newProject = {
    id: `proj_${Date.now()}`,
    ...projectData,
    createdBy: "current.user@landworth.com",
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  // Clear cache to force refresh
  cache.clear();

  return { project: newProject };
};

/**
 * Update an existing project
 */
export const updateProject = async (id, projectData) => {
  if (!id) {
    throw new APIError("Project ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  const updatedProject = {
    ...projectData,
    id,
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  // Clear cache to force refresh
  cache.clear();

  return { project: updatedProject };
};

/**
 * Delete a project
 */
export const deleteProject = async (id) => {
  if (!id) {
    throw new APIError("Project ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  // Clear cache to force refresh
  cache.clear();

  return { success: true, message: `Project '${id}' deleted successfully` };
};

// ===== DEALS API =====

/**
 * Get all deals with optional filtering
 */
export const getDeals = async (params = {}) => {
  return apiRequest("deals", params);
};

/**
 * Get a single deal by ID
 */
export const getDeal = async (id) => {
  if (!id) {
    throw new APIError("Deal ID is required", "VALIDATION_ERROR");
  }

  const data = await apiRequest("deals");
  const deal = data.deals.find((d) => d.id === id);

  if (!deal) {
    throw new APIError(`Deal with ID '${id}' not found`, "NOT_FOUND");
  }

  return { deal };
};

/**
 * Create a new deal
 */
export const createDeal = async (dealData) => {
  await simulateNetworkDelay();

  const newDeal = {
    id: `deal_${Date.now()}`,
    ...dealData,
    createdBy: "current.user@landworth.com",
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  cache.clear();
  return { deal: newDeal };
};

/**
 * Update an existing deal
 */
export const updateDeal = async (id, dealData) => {
  if (!id) {
    throw new APIError("Deal ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  const updatedDeal = {
    ...dealData,
    id,
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  cache.clear();
  return { deal: updatedDeal };
};

/**
 * Delete a deal
 */
export const deleteDeal = async (id) => {
  if (!id) {
    throw new APIError("Deal ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();
  cache.clear();

  return { success: true, message: `Deal '${id}' deleted successfully` };
};

// ===== VALUATIONS API =====

/**
 * Get all valuations with optional filtering
 */
export const getValuations = async (params = {}) => {
  return apiRequest("valuations", params);
};

/**
 * Get a single valuation by ID
 */
export const getValuation = async (id) => {
  if (!id) {
    throw new APIError("Valuation ID is required", "VALIDATION_ERROR");
  }

  const data = await apiRequest("valuations");
  const valuation = data.valuations.find((v) => v.id === id);

  if (!valuation) {
    throw new APIError(`Valuation with ID '${id}' not found`, "NOT_FOUND");
  }

  return { valuation };
};

/**
 * Create a new valuation
 */
export const createValuation = async (valuationData) => {
  await simulateNetworkDelay();

  const newValuation = {
    id: `val_${Date.now()}`,
    ...valuationData,
    createdBy: "current.user@landworth.com",
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  cache.clear();
  return { valuation: newValuation };
};

/**
 * Update an existing valuation
 */
export const updateValuation = async (id, valuationData) => {
  if (!id) {
    throw new APIError("Valuation ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  const updatedValuation = {
    ...valuationData,
    id,
    updatedBy: "current.user@landworth.com",
    lastUpdated: new Date().toISOString(),
  };

  cache.clear();
  return { valuation: updatedValuation };
};

/**
 * Delete a valuation
 */
export const deleteValuation = async (id) => {
  if (!id) {
    throw new APIError("Valuation ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();
  cache.clear();

  return { success: true, message: `Valuation '${id}' deleted successfully` };
};

// ===== AUTOMATION FUNCTIONS =====

/**
 * Get all automations
 */
export const getAutomations = async (params = {}) => {
  try {
    return await apiRequest("automations", params);
  } catch (error) {
    throw new APIError(`Failed to fetch automations: ${error.message}`);
  }
};

/**
 * Get a single automation by ID
 */
export const getAutomation = async (id) => {
  if (!id) {
    throw new APIError("Automation ID is required", "VALIDATION_ERROR");
  }

  try {
    const response = await getAutomations();
    const automation = response.automations?.find((a) => a.id === id);

    if (!automation) {
      throw new APIError(`Automation with ID ${id} not found`, "NOT_FOUND");
    }

    return { automation };
  } catch (error) {
    throw new APIError(`Failed to fetch automation: ${error.message}`);
  }
};

/**
 * Create a new automation
 */
export const createAutomation = async (automationData) => {
  await simulateNetworkDelay();

  // Basic validation
  if (!automationData.name || !automationData.description) {
    throw new APIError(
      "Missing required fields: name and description",
      "VALIDATION_ERROR"
    );
  }

  // Simulate server processing
  const newAutomation = {
    id: `auto-${Date.now()}`,
    ...automationData,
    totalRuns: 0,
    inProgress: 0,
    successfulRuns: 0,
    failedRuns: 0,
    status: automationData.status || "draft",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  cache.clear();
  return { automation: newAutomation };
};

/**
 * Update an existing automation
 */
export const updateAutomation = async (id, automationData) => {
  if (!id) {
    throw new APIError("Automation ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  const automation = await getAutomation(id);
  const updatedAutomation = {
    ...automation.automation,
    ...automationData,
    id, // Ensure ID cannot be changed
    updatedAt: new Date().toISOString(),
  };

  cache.clear();
  return { automation: updatedAutomation };
};

/**
 * Delete an automation
 */
export const deleteAutomation = async (id) => {
  if (!id) {
    throw new APIError("Automation ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  // Check if automation exists
  await getAutomation(id);

  cache.clear();
  return { success: true, message: `Automation ${id} deleted successfully` };
};

/**
 * Trigger an automation manually
 */
export const triggerAutomation = async (id, context = {}) => {
  if (!id) {
    throw new APIError("Automation ID is required", "VALIDATION_ERROR");
  }

  await simulateNetworkDelay();

  const automation = await getAutomation(id);

  if (automation.automation.status !== "active") {
    throw new APIError("Cannot trigger inactive automation", "INVALID_STATE");
  }

  return {
    success: true,
    message: `Automation ${id} triggered successfully`,
    executionId: `exec-${Date.now()}`,
    estimatedDuration:
      automation.automation.metrics?.averageExecutionTime || "2 minutes",
  };
};

/**
 * Attach an automation to a deal
 */
export const attachAutomationToDeal = async (dealId, automationId) => {
  if (!dealId || !automationId) {
    throw new APIError(
      "Deal ID and Automation ID are required",
      "VALIDATION_ERROR"
    );
  }

  await simulateNetworkDelay();

  // In a real implementation, this would make an API call to attach the automation
  // For now, we'll simulate success
  cache.clear(); // Clear cache to force refresh

  return {
    success: true,
    message: `Automation ${automationId} attached to deal ${dealId} successfully`,
    attachmentId: `attachment-${Date.now()}`,
  };
};

/**
 * Detach an automation from a deal
 */
export const detachAutomationFromDeal = async (dealId, automationId) => {
  if (!dealId || !automationId) {
    throw new APIError(
      "Deal ID and Automation ID are required",
      "VALIDATION_ERROR"
    );
  }

  await simulateNetworkDelay();

  // In a real implementation, this would make an API call to detach the automation
  // For now, we'll simulate success
  cache.clear(); // Clear cache to force refresh

  return {
    success: true,
    message: `Automation ${automationId} detached from deal ${dealId} successfully`,
  };
};

/**
 * Get automation templates and configuration options
 */
export const getAutomationTemplates = async () => {
  try {
    const response = await apiRequest("automations");
    return {
      templates: response.automationTemplates || [],
      triggers: response.triggers || [],
      statusOptions: response.statusOptions || [],
      typeOptions: response.typeOptions || [],
    };
  } catch (error) {
    throw new APIError(
      `Failed to fetch automation templates: ${error.message}`
    );
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Get dashboard statistics
 */
export const getDashboardStats = async () => {
  try {
    const [projectsData, dealsData, valuationsData, automationsData] =
      await Promise.all([
        getProjects(),
        getDeals(),
        getValuations(),
        getAutomations(),
      ]);

    return {
      projects: {
        total: projectsData.projects.length,
        active: projectsData.projects.filter((p) => p.status === "active")
          .length,
        completed: projectsData.projects.filter((p) => p.status === "completed")
          .length,
        totalValue: projectsData.projects.reduce(
          (sum, p) => sum + (p.financial?.totalCost || 0),
          0
        ),
      },
      deals: {
        total: dealsData.deals.length,
        active: dealsData.deals.filter((d) => d.status === "active").length,
        pending: dealsData.deals.filter((d) => d.status === "pending").length,
        totalValue: dealsData.deals.reduce(
          (sum, d) => sum + (d.financial?.totalDealValue || 0),
          0
        ),
      },
      valuations: {
        total: valuationsData.valuations.length,
        approved: valuationsData.valuations.filter(
          (v) => v.status === "approved"
        ).length,
        pending: valuationsData.valuations.filter((v) => v.status === "pending")
          .length,
        totalValue: valuationsData.valuations.reduce(
          (sum, v) => sum + (v.valuation?.marketValue || v.valuation?.gdv || 0),
          0
        ),
      },
      automations: {
        total: automationsData.automations.length,
        active: automationsData.automations.filter((a) => a.status === "active")
          .length,
        archived: automationsData.automations.filter(
          (a) => a.status === "archived"
        ).length,
        totalRuns: automationsData.automations.reduce(
          (sum, a) => sum + (a.totalRuns || 0),
          0
        ),
        inProgress: automationsData.automations.reduce(
          (sum, a) => sum + (a.inProgress || 0),
          0
        ),
      },
    };
  } catch (error) {
    throw new APIError(
      "Failed to fetch dashboard statistics",
      "DASHBOARD_ERROR",
      error
    );
  }
};

/**
 * Search across all entities
 */
export const globalSearch = async (
  searchTerm,
  entityTypes = ["projects", "deals", "valuations", "automations"]
) => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    throw new APIError(
      "Search term must be at least 2 characters",
      "VALIDATION_ERROR"
    );
  }

  const searchParams = { search: searchTerm.trim() };
  const results = {};

  try {
    if (entityTypes.includes("projects")) {
      const projectsData = await getProjects(searchParams);
      results.projects = projectsData.projects;
    }

    if (entityTypes.includes("deals")) {
      const dealsData = await getDeals(searchParams);
      results.deals = dealsData.deals;
    }

    if (entityTypes.includes("valuations")) {
      const valuationsData = await getValuations(searchParams);
      results.valuations = valuationsData.valuations;
    }

    if (entityTypes.includes("automations")) {
      const automationsData = await getAutomations(searchParams);
      results.automations = automationsData.automations;
    }

    return {
      searchTerm,
      results,
      totalResults: Object.values(results).reduce(
        (sum, items) => sum + items.length,
        0
      ),
    };
  } catch (error) {
    throw new APIError("Search failed", "SEARCH_ERROR", error);
  }
};

/**
 * Clear API cache
 */
export const clearCache = () => {
  cache.clear();
  return { success: true, message: "Cache cleared successfully" };
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()),
    lastCleared: cache.size === 0 ? "Never" : "Unknown",
  };
};

// Export the APIError class for external use
export { APIError };
